import { Button } from "primereact/button";
import { useEffect, useRef, useState } from "react";
import type { UnderwritingRationale } from "../../../client";
import { displayNameForUser } from "../../../util";
import { useUpdateUnderwritingRationale, useUserById } from "../data";
import { formatTimestamp } from "../utils";
import styles from "./UnderwritingRationalePanel.module.css";

const SAVE_SHORTCUT = /Mac|iPhone|iPad/.test(navigator.userAgent)
  ? "⌘S"
  : "Ctrl+S";

/**
 * Side-panel editor for a report's underwriting rationale. Edits stay local
 * until saved, closing with unsaved edits asks before discarding them, and
 * closing is disabled while a save is in flight.
 */
export const UnderwritingRationalePanel = ({
  reportId,
  rationale,
  onClose,
}: {
  reportId: string;
  rationale: UnderwritingRationale | null | undefined;
  onClose: () => void;
}) => {
  const save = useUpdateUnderwritingRationale(reportId);
  const author = displayNameForUser(
    useUserById(rationale?.last_updated_user_id)
  );
  const savedText = rationale?.text ?? "";
  const [draft, setDraft] = useState(savedText);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dirty = draft !== savedText;
  const showDiscardPrompt = confirmingDiscard && dirty;

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);

  // Covers reloads and closing the tab. In-app navigation is not guarded.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Older Safari and Chrome only prompt when returnValue is set.
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const onSave = () => {
    // One save at a time, so responses cannot land out of order.
    if (dirty && !save.isPending) save.mutate(draft);
  };

  const requestClose = () => (dirty ? setConfirmingDiscard(true) : onClose());

  const keepEditing = () => {
    setConfirmingDiscard(false);
    textareaRef.current?.focus();
  };

  const isSaved = !dirty && !save.isPending && rationale != null;
  const saveLabel = save.isPending ? "Saving" : isSaved ? "Saved" : "Save";
  const savedAt = rationale
    ? formatTimestamp(rationale.last_updated_ts)
    : undefined;

  return (
    <section className={styles.panel} aria-label="Underwriting rationale">
      <header className={styles.header}>
        <span className={styles.title}>Underwriting rationale</span>
        {dirty && (
          <span className={styles.unsaved}>
            <span className={styles.unsavedDot} />
            Unsaved
          </span>
        )}
        <Button
          icon="pi pi-times"
          text
          rounded
          size="small"
          severity="secondary"
          aria-label="Close underwriting rationale"
          className={styles.close}
          // The draft lives only in this panel. If the save fails, closing
          // first would lose the edit.
          disabled={save.isPending}
          onClick={requestClose}
        />
      </header>
      <textarea
        ref={textareaRef}
        className={styles.textarea}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          setConfirmingDiscard(false);
        }}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
            e.preventDefault();
            onSave();
          }
        }}
        placeholder="Why this risk, at these terms? Key exposures, pricing logic, conditions."
        spellCheck
      />
      <footer className={styles.footer}>
        {showDiscardPrompt ? (
          <>
            <span className={styles.prompt}>Discard changes?</span>
            <Button
              label="Keep editing"
              size="small"
              text
              severity="secondary"
              onClick={keepEditing}
            />
            <Button
              label="Discard"
              size="small"
              severity="danger"
              onClick={onClose}
            />
          </>
        ) : (
          <>
            <span
              className={styles.meta}
              title={
                savedAt &&
                `Last saved${author ? ` by ${author}` : ""} on ${savedAt}`
              }
            >
              {author && <span className={styles.author}>{author} · </span>}
              {savedAt}
            </span>
            <Button
              size="small"
              label={saveLabel}
              icon={isSaved ? "pi pi-check" : undefined}
              loading={save.isPending}
              disabled={!dirty || save.isPending}
              text={!dirty}
              severity={dirty ? undefined : "secondary"}
              className={styles.save}
              onClick={onSave}
            >
              {dirty && !save.isPending && (
                <kbd className={styles.kbd}>{SAVE_SHORTCUT}</kbd>
              )}
            </Button>
          </>
        )}
      </footer>
    </section>
  );
};
